import { useEffect, useReducer } from 'react';
import { OrderData } from '../../../api';

export const ERROR_OVERLAY_DURATION_MS = 3000;
export const DEDUP_WINDOW_MS = 3000;

export type ErrorReason =
  | 'invalid_qr'
  | 'wrong_event'
  | 'wrong_account'
  | 'order_not_in_roster'
  | 'order_cancelled'
  | 'already_checked_in'
  | 'external_voucher_not_approved'
  | 'network_error';

export type ErrorContext = {
  reason: ErrorReason;
  checkedInBy?: string;
  checkedInAt?: string;
  networkMessage?: string;
};

export type Phase =
  | { kind: 'scanning' }
  | { kind: 'processing'; qr: string }
  | { kind: 'success_overlay'; order: OrderData }
  | { kind: 'error_overlay'; error: ErrorContext }
  | { kind: 'search_mode' }
  | { kind: 'permission_denied' };

type ScannerState = {
  phase: Phase;
  lastScannedQr: string | null;
  lastScannedAt: number | null;
  torchOn: boolean;
};

type Action =
  | { type: 'SCAN_RECEIVED'; qr: string; at: number }
  | { type: 'PROCESSING_STARTED' }
  | { type: 'CHECKIN_SUCCESS'; order: OrderData }
  | { type: 'CHECKIN_ERROR'; error: ErrorContext }
  | { type: 'OVERLAY_TIMEOUT' }
  | { type: 'ENTER_SEARCH' }
  | { type: 'EXIT_SEARCH' }
  | { type: 'PERMISSION_DENIED' }
  | { type: 'SET_TORCH'; on: boolean }
  | { type: 'RESET' };

const initialState: ScannerState = {
  phase: { kind: 'scanning' },
  lastScannedQr: null,
  lastScannedAt: null,
  torchOn: false,
};

const reducer = (state: ScannerState, action: Action): ScannerState => {
  switch (action.type) {
    case 'SCAN_RECEIVED': {
      if (state.phase.kind !== 'scanning') return state;
      if (
        state.lastScannedQr === action.qr &&
        state.lastScannedAt !== null &&
        action.at - state.lastScannedAt < DEDUP_WINDOW_MS
      ) {
        return state;
      }
      return {
        ...state,
        phase: { kind: 'processing', qr: action.qr },
        lastScannedQr: action.qr,
        lastScannedAt: action.at,
      };
    }
    case 'CHECKIN_SUCCESS':
      return { ...state, phase: { kind: 'success_overlay', order: action.order } };
    case 'CHECKIN_ERROR':
      return { ...state, phase: { kind: 'error_overlay', error: action.error } };
    case 'OVERLAY_TIMEOUT':
      if (state.phase.kind === 'success_overlay' || state.phase.kind === 'error_overlay') {
        return { ...state, phase: { kind: 'scanning' } };
      }
      return state;
    case 'ENTER_SEARCH':
      return { ...state, phase: { kind: 'search_mode' } };
    case 'EXIT_SEARCH':
      return { ...state, phase: { kind: 'scanning' } };
    case 'PERMISSION_DENIED':
      return { ...state, phase: { kind: 'permission_denied' } };
    case 'SET_TORCH':
      return { ...state, torchOn: action.on };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
};

export const useCheckinScannerMachine = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (state.phase.kind !== 'error_overlay') return undefined;
    const timer = window.setTimeout(() => dispatch({ type: 'OVERLAY_TIMEOUT' }), ERROR_OVERLAY_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [state.phase.kind]);

  return { state, dispatch };
};
